var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import client from "../../http/client";
import { useCart, useOrderIntent } from "../../providers";
import { getDisplayedOrderTotal } from "../utility/cartTotals";
import { useCreditPaymentContext } from "./CreditPaymentContext";
import { PaymentErrorNotice } from "./PaymentErrorNotice";
import { RadixLoading } from "../primitives/RadixLoading";
import { OrderSummary } from "../orderSummary";
import { createPaymentAttemptId } from "./paymentAttemptId";
import { getRecaptchaCardLastFour, getRecaptchaToken } from "./recaptcha";
var errorMessageMap = {
    "The Account field is not a valid credit card number.": "Please check the card number and try again.",
    "Invalid Expiration Month": "Please enter a valid expiration month (MM).",
    "Invalid Expiration Year": "Please enter a valid expiration year.",
    "Invalid CVV": "Please check the security code (CVV) and try again.",
    "Token Privileges Suspended": "Too many failed attempts. Please wait 20 minutes before trying again.",
};
var isAppleDevice = function () {
    var userAgent = window.navigator.userAgent.toLowerCase();
    return userAgent.includes("iphone") || userAgent.includes("ipad") || userAgent.includes("macintosh");
};
var getAmountFromCart = function (cart) {
    var total = getDisplayedOrderTotal(cart, true);
    return total.toFixed(2);
};
var hasString = function (value) {
    return typeof value === "string" && value.trim().length > 0;
};
var isWalletSupportEnabled = function (value) {
    if (typeof value === "undefined" || value === null || value === "") {
        return true;
    }
    return Boolean(Number(value));
};
var GOOGLE_PAY_SDK_URL = "https://pay.google.com/gp/p/js/pay.js";
var DATACAP_APPLE_PAY_CSS_URLS = new Set([
    "https://wallet.dcap.com/css/applepay.css",
    "https://wallet-cert.dcap.com/css/applepay.css",
]);
var DATACAP_APPLE_PAY_SAFE_STYLE_ID = "arrowpay-datacap-apple-pay-safe-style";
var DATACAP_APPLE_PAY_SAFE_STYLES = "\n    .vertical-checkout input,\n    .vertical-checkout select,\n    .vertical-checkout textarea {\n        min-width: 0 !important;\n        max-width: 100% !important;\n    }\n    .vertical-checkout div.apple-pay {\n        max-width: 100% !important;\n        width: 100%;\n        height: 46px;\n    }\n    @supports (-webkit-appearance: -apple-pay-button) {\n        .apple-pay-button {\n            display: inline-block;\n            -webkit-appearance: -apple-pay-button;\n        }\n        .apple-pay-button-black {\n            -apple-pay-button-style: black;\n        }\n        .apple-pay-button-white {\n            -apple-pay-button-style: white;\n        }\n        .apple-pay-button-white-with-line {\n            -apple-pay-button-style: white-outline;\n        }\n    }\n";
var loadGooglePaySdk = function () {
    return new Promise(function (resolve, reject) {
        var _a;
        var googleWindow = window;
        if (typeof ((_a = googleWindow.google) === null || _a === void 0 ? void 0 : _a.payments) !== "undefined") {
            resolve();
            return;
        }
        var existingScript = document.getElementById("arrowpay-google-pay-sdk");
        if (existingScript) {
            var onLoad = function () { return resolve(); };
            var onError = function () { return reject(new Error("Google Pay SDK script failed to load")); };
            existingScript.addEventListener("load", onLoad, { once: true });
            existingScript.addEventListener("error", onError, { once: true });
            return;
        }
        var script = document.createElement("script");
        script.id = "arrowpay-google-pay-sdk";
        script.src = GOOGLE_PAY_SDK_URL;
        script.async = true;
        script.onload = function () { return resolve(); };
        script.onerror = function () { return reject(new Error("Google Pay SDK script failed to load")); };
        document.head.appendChild(script);
    });
};
export var ArrowPayDigitalPaymentForm = function (_a) {
    var _b, _c;
    var _d = _a.embedded, embedded = _d === void 0 ? false : _d;
    var _e = useCreditPaymentContext(), cart = _e.cart, processorInfo = _e.processorInfo, selectedPaymentType = _e.selectedPaymentType, handleCreditOrderComplete = _e.handleCreditOrderComplete, paymentError = _e.paymentError, reportProcessorError = _e.reportProcessorError, clearPaymentError = _e.clearPaymentError, markPaymentSubmitting = _e.markPaymentSubmitting, checkoutComment = _e.checkoutComment, billingInfo = _e.billingInfo, checkoutUserId = _e.checkoutUserId, isCurbsideOrder = _e.isCurbsideOrder, shouldSendSmsAlerts = _e.shouldSendSmsAlerts, hasMarketingOptIn = _e.hasMarketingOptIn, smsNumber = _e.smsNumber, signUpRewards = _e.signUpRewards;
    var checkoutCart = useCart().cart;
    var currentAddress = useOrderIntent().currentAddress;
    var _f = useState(false), loading = _f[0], setLoading = _f[1];
    var _g = useState(false), walletInitialized = _g[0], setWalletInitialized = _g[1];
    var _h = useState(false), googlePayReady = _h[0], setGooglePayReady = _h[1];
    var _j = useState(null), walletButtonContainer = _j[0], setWalletButtonContainer = _j[1];
    var buttonObserverRef = useRef(null);
    var cssObserverRef = useRef(null);
    var walletInitAttemptedRef = useRef(false);
    var paymentAttemptIdRef = useRef(null);
    var appleDevice = useMemo(function () { return isAppleDevice(); }, []);
    var tokenKey = hasString(processorInfo === null || processorInfo === void 0 ? void 0 : processorInfo.token_key) ? processorInfo.token_key : "";
    var appleMerchantName = hasString(processorInfo === null || processorInfo === void 0 ? void 0 : processorInfo.applepay_wallet_merchant_name) ? processorInfo.applepay_wallet_merchant_name : "";
    var appleMerchantId = hasString(processorInfo === null || processorInfo === void 0 ? void 0 : processorInfo.applepay_wallet_mid) ? processorInfo.applepay_wallet_mid : "";
    var appleButtonId = hasString(processorInfo === null || processorInfo === void 0 ? void 0 : processorInfo.applepay_button_id) ? processorInfo.applepay_button_id : "";
    var googleMerchantName = hasString(processorInfo === null || processorInfo === void 0 ? void 0 : processorInfo.googlepay_wallet_merchant_name) ? processorInfo.googlepay_wallet_merchant_name : "";
    var googleMerchantId = hasString(processorInfo === null || processorInfo === void 0 ? void 0 : processorInfo.googlepay_wallet_mid) ? processorInfo.googlepay_wallet_mid : "";
    var googleButtonId = hasString(processorInfo === null || processorInfo === void 0 ? void 0 : processorInfo.googlepay_button_id) ? processorInfo.googlepay_button_id : "";
    var sessionStore = ((_b = window.session) === null || _b === void 0 ? void 0 : _b.store) || {};
    var supportsApplePay = isWalletSupportEnabled(sessionStore.supports_applepay);
    var supportsGooglePay = isWalletSupportEnabled(sessionStore.supports_googlepay);
    var amountSourceCart = (_c = checkoutCart) !== null && _c !== void 0 ? _c : cart;
    var amount = getAmountFromCart(amountSourceCart);
    var amountValue = Number(amount);
    var hasPayableAmount = Number.isFinite(amountValue) && amountValue > 0;
    var applePayWindow = window;
    var hasApplePaySession = Boolean(applePayWindow.ApplePaySession
        && typeof applePayWindow.ApplePaySession.canMakePayments === "function"
        && applePayWindow.ApplePaySession.canMakePayments());
    var canRenderApplePay = appleDevice
        && supportsApplePay
        && hasApplePaySession
        && tokenKey.length > 0
        && appleMerchantName.length > 0
        && appleMerchantId.length > 0
        && appleButtonId.length > 0
        && hasPayableAmount;
    var canRenderGooglePay = supportsGooglePay
        && !canRenderApplePay
        && googlePayReady
        && tokenKey.length > 0
        && googleMerchantName.length > 0
        && googleMerchantId.length > 0
        && googleButtonId.length > 0
        && hasPayableAmount;
    var shouldUseApplePay = selectedPaymentType === "apple_pay";
    var shouldUseGooglePay = selectedPaymentType === "google_pay";
    var walletType = shouldUseApplePay && canRenderApplePay
        ? "apple"
        : shouldUseGooglePay && canRenderGooglePay
            ? "google"
            : null;
    var canRenderWalletButton = walletType !== null;
    var merchantName = walletType === "apple" ? appleMerchantName : googleMerchantName;
    var merchantId = walletType === "apple" ? appleMerchantId : googleMerchantId;
    var buttonId = walletType === "apple" ? appleButtonId : googleButtonId;
    var walletDisplayName = walletType === "google" ? "Google Pay" : "Apple Pay";
    var processTokenResponse = useCallback(function (res) {
        var _a;
        setLoading(true);
        markPaymentSubmitting(true);
        clearPaymentError();
        if ((res === null || res === void 0 ? void 0 : res.Errors) && Array.isArray(res.Errors) && res.Errors.length > 0) {
            var rawMessage = res.Errors[0];
            var mappedMessage = (_a = errorMessageMap[rawMessage]) !== null && _a !== void 0 ? _a : rawMessage;
            reportProcessorError("arrowpay", { Message: mappedMessage });
            setLoading(false);
            markPaymentSubmitting(false);
            paymentAttemptIdRef.current = null;
            return;
        }
        if (!res || !Object.prototype.hasOwnProperty.call(res, "Token")) {
            reportProcessorError("arrowpay", { Message: "We could not process your payment. Please try again or use a different payment method" });
            setLoading(false);
            markPaymentSubmitting(false);
            paymentAttemptIdRef.current = null;
            return;
        }
        if (!paymentAttemptIdRef.current) {
            paymentAttemptIdRef.current = createPaymentAttemptId();
        }
        var shouldReleaseSubmitting = true;
        getRecaptchaToken(processorInfo === null || processorInfo === void 0 ? void 0 : processorInfo.recaptcha).then(function (recaptchaToken) { return client.post("/order/arrowpay", __assign({ payment_attempt_id: paymentAttemptIdRef.current, recaptcha_token: recaptchaToken, card_last_four: getRecaptchaCardLastFour(res.Last4), token: res.Token, cvv: res.CVV, address: billingInfo.billingAddress, zip: billingInfo.billingZipcode, comment: checkoutComment, should_save_card: false, using_on_file: false, address_id: currentAddress === null || currentAddress === void 0 ? void 0 : currentAddress.id, user_id: checkoutUserId, is_curbside_order: isCurbsideOrder, should_send_sms_alerts: shouldSendSmsAlerts, sms_number: smsNumber, has_marketing_opt_in: hasMarketingOptIn }, (signUpRewards ? { sign_up_rewards: true } : {}))); }).then(function (jres) {
            if (jres.status === 200) {
                shouldReleaseSubmitting = false;
                var orderJ = JSON.parse(jres.data.order_data);
                handleCreditOrderComplete(orderJ);
            }
        }).catch(function (err) {
            reportProcessorError("arrowpay", err);
        }).finally(function () {
            setLoading(false);
            if (shouldReleaseSubmitting) {
                paymentAttemptIdRef.current = null;
                markPaymentSubmitting(false);
            }
        });
    }, [
        billingInfo.billingAddress,
        billingInfo.billingZipcode,
        checkoutComment,
        checkoutUserId,
        clearPaymentError,
        currentAddress === null || currentAddress === void 0 ? void 0 : currentAddress.id,
        handleCreditOrderComplete,
        isCurbsideOrder,
        hasMarketingOptIn,
        markPaymentSubmitting,
        reportProcessorError,
        processorInfo === null || processorInfo === void 0 ? void 0 : processorInfo.recaptcha,
        signUpRewards,
        shouldSendSmsAlerts,
        smsNumber,
    ]);
    var tokenCallback = processTokenResponse;
    useEffect(function () {
        if (!supportsGooglePay) {
            setGooglePayReady(false);
            return;
        }
        var checkoutWindow = window;
        var getGooglePaymentsClient = checkoutWindow.getGooglePaymentsClient;
        var getGoogleIsReadyToPayRequest = checkoutWindow.getGoogleIsReadyToPayRequest;
        if (typeof getGooglePaymentsClient !== "function" || typeof getGoogleIsReadyToPayRequest !== "function") {
            setGooglePayReady(true);
            return;
        }
        var isActive = true;
        var checkGooglePayReadiness = function () { return __awaiter(void 0, void 0, void 0, function () {
            var client_1, response, err_1;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 3, , 4]);
                        return [4 /*yield*/, loadGooglePaySdk()];
                    case 1:
                        _a.sent();
                        client_1 = getGooglePaymentsClient();
                        return [4 /*yield*/, client_1.isReadyToPay(getGoogleIsReadyToPayRequest())];
                    case 2:
                        response = _a.sent();
                        if (isActive) {
                            setGooglePayReady(!!response.result);
                        }
                        return [3 /*break*/, 4];
                    case 3:
                        err_1 = _a.sent();
                        console.error("Google Pay readiness check failed", err_1);
                        if (isActive) {
                            setGooglePayReady(false);
                        }
                        return [3 /*break*/, 4];
                    case 4: return [2 /*return*/];
                }
            });
        }); };
        void checkGooglePayReadiness();
        return function () {
            isActive = false;
        };
    }, [supportsGooglePay]);
    useEffect(function () {
        setWalletInitialized(false);
        walletInitAttemptedRef.current = false;
    }, [walletType, buttonId, amount]);
    useEffect(function () {
        if (!canRenderWalletButton || walletInitialized) {
            return;
        }
        if (!walletButtonContainer) {
            return;
        }
        var datacapApplePay = window.DatacapApplePay;
        var datacapGooglePay = window.DatacapGooglePay;
        if (walletType === "apple" && typeof (datacapApplePay === null || datacapApplePay === void 0 ? void 0 : datacapApplePay.init) !== "function") {
            return;
        }
        if (walletType === "google" && typeof (datacapGooglePay === null || datacapGooglePay === void 0 ? void 0 : datacapGooglePay.init) !== "function") {
            return;
        }
        if (buttonObserverRef.current) {
            buttonObserverRef.current.disconnect();
            buttonObserverRef.current = null;
        }
        if (cssObserverRef.current) {
            cssObserverRef.current.disconnect();
            cssObserverRef.current = null;
        }
        var applyWalletWidth = function () {
            if (walletButtonContainer.classList.contains("apple-pay")) {
                walletButtonContainer.style.maxWidth = "100%";
            }
        };
        var neutralizeDatacapApplePayCss = function () {
            var links = Array.from(document.querySelectorAll('link[rel="stylesheet"]'));
            var datacapCssDetected = false;
            links.forEach(function (linkNode) {
                var href = linkNode.getAttribute("href");
                if (href && DATACAP_APPLE_PAY_CSS_URLS.has(href)) {
                    datacapCssDetected = true;
                    linkNode.disabled = true;
                }
            });
            if (!datacapCssDetected) {
                return;
            }
            var existingOverride = document.getElementById(DATACAP_APPLE_PAY_SAFE_STYLE_ID);
            if (existingOverride) {
                return;
            }
            var safeStyleElement = document.createElement("style");
            safeStyleElement.id = DATACAP_APPLE_PAY_SAFE_STYLE_ID;
            safeStyleElement.textContent = DATACAP_APPLE_PAY_SAFE_STYLES;
            document.head.appendChild(safeStyleElement);
        };
        buttonObserverRef.current = new MutationObserver(function () {
            applyWalletWidth();
        });
        buttonObserverRef.current.observe(walletButtonContainer, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ["class"],
        });
        cssObserverRef.current = new MutationObserver(function () {
            neutralizeDatacapApplePayCss();
        });
        cssObserverRef.current.observe(document.head, {
            childList: true,
            subtree: true,
        });
        applyWalletWidth();
        neutralizeDatacapApplePayCss();
        if (walletType === "google") {
            if (!datacapGooglePay) {
                return;
            }
            if (walletInitAttemptedRef.current) {
                return;
            }
            walletInitAttemptedRef.current = true;
            walletButtonContainer.innerHTML = "";
            loadGooglePaySdk().then(function () {
                try {
                    datacapGooglePay.init(tokenCallback, tokenKey, merchantName, merchantId, amount);
                    setWalletInitialized(true);
                }
                catch (error) {
                    reportProcessorError("arrowpay", { Message: "Google Pay failed to initialize. Please try another payment method." });
                }
            }).catch(function (error) {
                void error;
                reportProcessorError("arrowpay", { Message: "Google Pay failed to initialize. Please try another payment method." });
            });
            return;
        }
        else {
            if (!datacapApplePay) {
                return;
            }
            datacapApplePay.init(tokenCallback, tokenKey, merchantName, merchantId, amount);
        }
        setWalletInitialized(true);
    }, [amount, appleButtonId, appleDevice, appleMerchantId, appleMerchantName, buttonId, canRenderWalletButton, googleButtonId, googleMerchantId, googleMerchantName, hasPayableAmount, merchantId, merchantName, reportProcessorError, supportsApplePay, supportsGooglePay, tokenCallback, tokenKey, walletButtonContainer, walletDisplayName, walletInitialized, walletType]);
    useEffect(function () {
        return function () {
            if (buttonObserverRef.current) {
                buttonObserverRef.current.disconnect();
                buttonObserverRef.current = null;
            }
            if (cssObserverRef.current) {
                cssObserverRef.current.disconnect();
                cssObserverRef.current = null;
            }
        };
    }, []);
    var formClassName = embedded
        ? "radix-credit-payment-form--arrowpay-digital"
        : "radix-credit-payment-form radix-credit-payment-form--arrowpay-digital";
    if (!canRenderWalletButton) {
        return (React.createElement("div", { className: formClassName, role: "region", "aria-label": "Digital payment details" },
            React.createElement(PaymentErrorNotice, { error: paymentError }),
            React.createElement("p", { className: "radix-credit-payment-description" }, "Digital wallet payment is currently unavailable on this device. Please choose another payment method.")));
    }
    return (React.createElement("div", { className: formClassName, role: "region", "aria-label": "Digital payment details" },
        React.createElement(PaymentErrorNotice, { error: paymentError }),
        React.createElement(OrderSummary, { isCardPaymentSelectedOverride: true }),
        loading ? React.createElement(RadixLoading, { label: "Processing payment" }) : null,
        React.createElement("div", { style: { margin: '1rem' } }),
        React.createElement("div", { hidden: loading },
            React.createElement("div", { className: "arrowpay-digital-wallet-wrapper", id: buttonId, ref: setWalletButtonContainer, role: "button", "aria-label": walletDisplayName }))));
};
